import React, { useState } from 'react';
import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import Tooltip from '@dashboard/components/Tooltip';
import clsx from 'clsx';
import { XCircleIcon } from '@heroicons/react/24/outline';
import Link from '@/dashboard/components/Link';
import { formatPriceForInput } from '@/dashboard/services/formatting/price';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';
import { formatPrice, formatPriceInput } from '../../../services/formatting/price';
import { calculateMaximumRefundAmount } from '../../../services/payments';
import { chargeKey } from '@dashboard/lib/queryKeys';
import isAbandonedAbandonment from '@/dashboard/services/abandonment/isAbandonedAbandonment';

const PaymentsBookingCancelDialog = ({ payment }) => {
  const [open, setOpen] = useState(false);
  const { token } = useAuth();
  const { emitToast } = useToast();
  const queryClient = useQueryClient();
  const maximumRefundAmount = calculateMaximumRefundAmount(payment);

  const cancelCharge = useMutation({
    mutationFn: values =>
      fetcher(`/sellers/${payment.seller}/charges/${payment._id}/cancel`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: resp => {
      queryClient.invalidateQueries({ queryKey: chargeKey(payment._id) });
      emitToast({
        title: 'Booking canceled',
        description: `Additionally ${resp.canceled} tickets were also canceled.`,
        type: 'success',
      });

      setOpen(false);
    },
    onError: error => {
      emitToast({
        title: 'An error occurred while canceling the booking',
        description: error?.message,
        type: 'error',
      });
    },
  });

  const tooltipLabel = payment.orderStatus === 'canceled' ? 'This booking has been canceled' : '';

  return (
    <>
      <Tooltip triggerClassName="w-full" label={tooltipLabel} side="right">
        <Link
          onClick={setOpen}
          className={clsx('w-full flex gap-x-2 items-center no-underline', {
            'pointer-events-none opacity-50 cursor-not-allowed':
              payment.orderStatus === 'canceled' || isAbandonedAbandonment(payment),
          })}
        >
          <>
            <XCircleIcon className="w-4 h-4" />
            Cancel booking
          </>
        </Link>
      </Tooltip>

      <Dialog
        title="Cancel booking"
        description={
          <>
            <div>
              Canceling a booking voids its tickets. Optionally issue a partial or full refund.
            </div>
          </>
        }
        cancelButton={null}
        confirmButton={null}
        size="md"
        open={open}
      >
        {() => (
          <Form
            showState={false}
            initialValues={{
              reason: '',
              sendCancellationEmail: true,
              includeRefund: false,
              refundAmount: formatPriceForInput({
                amount: maximumRefundAmount,
                currency: payment.currency,
                style: 'decimal',
              }),
              sendRefundEmail: true,
            }}
            onSubmit={async values => {
              try {
                let data;
                if (values.includeRefund === true) {
                  data = await cancelCharge.mutateAsync({
                    ...values,
                    refundAmount: formatPriceInput(values.refundAmount),
                  });
                } else {
                  data = await cancelCharge.mutateAsync({
                    reason: values.reason,
                    sendCancellationEmail: values.sendCancellationEmail,
                    includeRefund: values.includeRefund,
                  });
                }

                return {
                  data,
                };
              } catch (error) {
                return {
                  error,
                };
              }
            }}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: chargeKey(payment._id) });

              emitToast({
                title: 'Booking canceled',
                type: 'success',
              });

              setOpen(false);
            }}
            fields={({ values }) => [
              {
                name: 'reason',
                label: 'Reason for cancellation',
                tags: ['not shown to customer'],
                type: 'textarea',
              },
              {
                name: 'sendCancellationEmail',
                label: 'Send cancellation email to customer',
                type: 'checkbox',
              },
              {
                name: 'includeRefund',
                label: 'Refund booking',
                type: 'checkbox',
                visible:
                  payment.status != 'refunded' &&
                  payment.stripePaymentIntentId != null &&
                  maximumRefundAmount > 0,
              },
              {
                name: 'refundAmount',
                label: `Refund amount`,
                tags: [
                  `${formatPrice({ amount: maximumRefundAmount, currency: payment.currency })} left to refund`,
                ],
                type: 'number',
                visible: values.includeRefund,
                required: values.includeRefund,
              },
              {
                name: 'refundReason',
                label: 'Refund reason',
                type: 'select',
                visible: values.includeRefund,
                required: values.includeRefund,
                options: [
                  { value: '', label: 'Select a reason' },
                  { value: 'duplicate', label: 'Duplicate' },
                  { value: 'fraudulent', label: 'Fraudulent' },
                  { value: 'requested_by_customer', label: 'Requested by customer' },
                ],
              },
              {
                name: 'refundReasonNote',
                label: 'Refund Note',
                tags: ['not shown to customer'],
                type: 'textarea',
                visible: values.includeRefund,
              },
              {
                name: 'sendRefundEmail',
                label: 'Send refund email to customer',
                type: 'checkbox',
                visible: values.includeRefund,
              },
            ]}
            formSubmitAlign="end"
            submitButton={{
              label: 'Cancel booking',
              appearance: 'primary',
            }}
          />
        )}
      </Dialog>
    </>
  );
};

export default PaymentsBookingCancelDialog;
